import { OPERATIONAL_METRIC_VALUE_TYPES } from '../../../constants/operationalMetricCatalog';
import MoneyMetricCard from './MoneyMetricCard';
import RankingChart from './RankingChart';
import RateMetricCard from './RateMetricCard';
import ScalarMetricCard from './ScalarMetricCard';
import StatusCountGrid from './StatusCountGrid';
import TimeSeriesChart from './TimeSeriesChart';

/**
 * @typedef {Object} MetricDisplayProps
 * @property {{ key: string, valueType: string, data: *, layoutSlot?: string }} metric
 * @property {boolean} [loading]
 * @property {boolean} [compactKpi]
 */

/** @type {Record<string, React.ComponentType>} */
export const METRIC_DISPLAY_BY_VALUE_TYPE = {
    [OPERATIONAL_METRIC_VALUE_TYPES.SCALAR]: ScalarMetricCard,
    [OPERATIONAL_METRIC_VALUE_TYPES.MONEY]: MoneyMetricCard,
    [OPERATIONAL_METRIC_VALUE_TYPES.RATE]: RateMetricCard,
    [OPERATIONAL_METRIC_VALUE_TYPES.STATUS_COUNT_LIST]: StatusCountGrid,
    [OPERATIONAL_METRIC_VALUE_TYPES.TIME_SERIES]: TimeSeriesChart,
    [OPERATIONAL_METRIC_VALUE_TYPES.VOLUME_SERIES]: TimeSeriesChart,
    [OPERATIONAL_METRIC_VALUE_TYPES.RANKING]: RankingChart,
};

/**
 * @param {string} valueType
 * @param {string} [layoutSlot]
 * @returns {React.ComponentType | null}
 */
export function resolveMetricDisplayComponent(valueType, layoutSlot) {
    if (valueType === OPERATIONAL_METRIC_VALUE_TYPES.VOLUME_SERIES && layoutSlot === 'volumeTotal') {
        return ScalarMetricCard;
    }
    return METRIC_DISPLAY_BY_VALUE_TYPE[valueType] || null;
}

/**
 * Maps registry component props from normalized metric.
 * @param {React.ComponentType} Component
 * @param {{ key: string, valueType: string, data: *, layoutSlot?: string }} metric
 * @param {{ loading?: boolean, compactKpi?: boolean }} options
 */
export function getMetricDisplayComponentProps(Component, metric, options = {}) {
    const { loading = false, compactKpi = false } = options;
    const { key, valueType, data, layoutSlot } = metric;

    switch (valueType) {
        case OPERATIONAL_METRIC_VALUE_TYPES.SCALAR:
        case OPERATIONAL_METRIC_VALUE_TYPES.MONEY:
        case OPERATIONAL_METRIC_VALUE_TYPES.RATE:
            return { metricKey: key, data, loading, compact: compactKpi };
        case OPERATIONAL_METRIC_VALUE_TYPES.VOLUME_SERIES:
            if (layoutSlot === 'volumeTotal') {
                return {
                    metricKey: key,
                    data: { value: data?.total ?? 0 },
                    loading,
                    compact: compactKpi,
                };
            }
            return { points: data?.points || [], loading };
        case OPERATIONAL_METRIC_VALUE_TYPES.STATUS_COUNT_LIST:
            return { items: data, loading };
        case OPERATIONAL_METRIC_VALUE_TYPES.TIME_SERIES:
            return { points: data, loading };
        case OPERATIONAL_METRIC_VALUE_TYPES.RANKING:
            return { metricKey: key, entries: data, loading };
        default:
            return { metricKey: key, data, loading };
    }
}
